/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { makeVar } from '@apollo/client';
import { FormEntry, mapQuestionToFormEntry } from '../../forms';
import { FormsIframeSavingStatus } from '../../models/formsIframe/FormsIframe';
import {
    ApprovalTemplateCategory,
    ApprovalTemplateInstance,
    DynamicRouteItem,
    DynamicRouteType,
    FallbackPolicyType,
    PeopleResult,
    PredefinedApproversRouteResolveResult,
    RouteResolveResultCode,
    RouteTypes,
    SelfOwnedApprovalTemplateInstance,
    UpdateApprovalTemplateInstanceInput,
    UpdateSelfOwnedApprovalTemplateInstanceInput,
    VisibilityPrincipal,
    VisibilityPrincipalInput,
    WorkflowSchema,
} from '../../service/gen/templates';
import { getAppHost } from '../../utilities/TeamsUtils';
import {
    DynamicRoute2UI_DynamicRouteInput,
    FallbackPolicy2UI_FallbackPolicyInput,
    MixedRouteType,
    StaticRoute2UI_StaticRouteInput,
    StepRouteSetting2UI_StepRouteSetting,
    UI_DynamicRouteInput,
    UI_FallbackPolicyInput,
    UI_StaticRouteInput,
    WorkflowSchema2WorkflowSchemaInput,
    WorkflowSchemaConvertor,
} from '../../utilities/TemplateUtils';
import type { UI_StepRouteSettingInput } from '../../views/components/CreateTemplateInstanceWizard/steps/WorkflowDesign/TemplateApproverSettings/TemplateSequentialApproverSettings';
import {
    currentScopeInCreationVar,
    currentTeamInCreationVar,
    TeamBasicInfo,
    TemplateScope,
} from './TemplateManagementVariables';

export enum CreateTemplateInstanceWizardSteps {
    BasicSettings = 1,
    FormDesign = 2,
    WorkflowSettings = 3,
}

export enum ApproverScope {
    LetSubmitterEnter,
    SpecifyApprovers,
}

export const currentStepVar = makeVar<CreateTemplateInstanceWizardSteps>(
    CreateTemplateInstanceWizardSteps.BasicSettings
);

export const currentMaxStepVar = makeVar<CreateTemplateInstanceWizardSteps>(
    CreateTemplateInstanceWizardSteps.BasicSettings
);

export const DefaultStaticRouteInput: UI_StaticRouteInput = {
    Approvers: [],
    AllMustRespond: true,
};

export const DefaultDynamicRouteInput: UI_DynamicRouteInput = {
    AllMustRespond: true,
    Items: [
        {
            Type: DynamicRouteType.DirectManager,
        },
    ],
};

export const DefaultFallbackPolicyInput: UI_FallbackPolicyInput = {
    Type: FallbackPolicyType.FallbackApprovers,
    FallbackApprovers: [],
    FallbackApproversAllMustRespond: true,
};

export const DefaultWorkflowSchemaV1: WorkflowSchema = {
    Version: 'v1.0',
    Approvers: [],
    AllMustRespond: true,
    IsCustomResponseEnabled: false,
};

export const DefaultWorkflowSchemaV2: WorkflowSchema = {
    Version: 'v2.0',
    Approvers: [],
    AllMustRespond: true,
    IsCustomResponseEnabled: false,
    RouteTypes: RouteTypes.LetUserEnter,
    StaticRoute: {
        AllMustRespond: true,
        ResolveResult: [],
    },
    DynamicRoute: {
        AllMustRespond: true,
        Items: [{ Type: DynamicRouteType.DirectManager, ResultCode: RouteResolveResultCode.Unresolved }],
    },
    FallbackPolicy: {
        Type: FallbackPolicyType.FallbackApprovers,
        FallbackApprovers: [],
        FallbackApproversAllMustRespond: true,
    },
};

export const getDefaultWorkflowSchema = (): WorkflowSchema => {
    const onlineWorkflowSchemaVersion = getAppHost().getEcsConfig().MicrosoftTeamApprovals.OnlineWorkflowSchemaVersion;
    if (onlineWorkflowSchemaVersion === 'v1.0') {
        return DefaultWorkflowSchemaV1;
    } else {
        return DefaultWorkflowSchemaV2;
    }
};

export const getValidWorkflowSchema = (instance: WorkflowSchema): WorkflowSchema => {
    if (instance?.StaticRoute?.ResolveResult?.length) {
        instance.StaticRoute.ResolveResult = instance.StaticRoute.ResolveResult.filter(
            (s: PredefinedApproversRouteResolveResult): boolean => s.ResultCode === RouteResolveResultCode.Success
        );
    }
    if (instance?.RouteTypes === RouteTypes.DynamicRoute) {
        return instance;
    } else if (instance?.DynamicRoute?.Items?.length) {
        instance.DynamicRoute.Items = instance.DynamicRoute.Items.filter(
            (d: DynamicRouteItem): boolean => d.ResultCode === RouteResolveResultCode.Success
        );
    }
    return instance;
};

// Template instance vars
export const templateInstanceIdVar = makeVar<string>('');
export const templateInstanceTitleVar = makeVar<string>('');
export const templateInstanceIconNameVar = makeVar<string>('');
export const templateInstanceIconBackgroundColorVar = makeVar<string>('');
export const templateInstanceDescriptionVar = makeVar<string>('');
export const templateInstanceSpecificPeopleVar = makeVar<VisibilityPrincipal[]>([]);
export const templateInstanceCategoryVar = makeVar<ApprovalTemplateCategory>(ApprovalTemplateCategory.NotSpecified);
export const templateInstanceWorkflowSchemaVersionVar = makeVar<string>('v1.0');
/* workflow schema v1.0 vars for indicating preset approvers of the template instance */
export const templateInstanceApproverScopeVar = makeVar<ApproverScope>(ApproverScope.LetSubmitterEnter);
export const templateInstanceApproversVar = makeVar<PeopleResult[]>([]);
export const templateInstanceAllMustRespondVar = makeVar<boolean>(true);
/* workflow schema v2.0 vars for indicating preset approvers of the template instance */
export const templateInstanceApproverRouteTypeVar = makeVar<RouteTypes | undefined>(RouteTypes.StaticRoute);
export const templateInstanceStaticRouteVar = makeVar<UI_StaticRouteInput>(DefaultStaticRouteInput);
export const templateInstanceDynamicRouteVar = makeVar<UI_DynamicRouteInput>(DefaultDynamicRouteInput);
export const templateInstanceFallbackPolicyVar = makeVar<UI_FallbackPolicyInput>(DefaultFallbackPolicyInput);
export const templateInstanceStatusVar = makeVar<boolean>(true); // indicate if template is enabled
export const templateInstanceIsCustomResponseEnabledVar = makeVar<boolean>(false);
export const templateInstanceCustomResponseOptionsVar = makeVar<string[]>([]);
export const templateInstanceFormIdVar = makeVar<string>(null);
export const templateInstanceFormEntriesVar = makeVar<FormEntry[]>([]);
export const templateInstanceIsFileAttachmentRequiredVar = makeVar<boolean>(false);
export const templateInstanceIsApproverCommentsRequiredVar = makeVar<boolean>(false);
export const templateInstanceStepRoutesVar = makeVar<UI_StepRouteSettingInput[]>([]);

export const updateTemplateInstanceInputCopyVar = makeVar<
    UpdateApprovalTemplateInstanceInput | UpdateSelfOwnedApprovalTemplateInstanceInput
>(null);

export const blockMainActionVar = makeVar<boolean>(false);
export const formsIframeSavingStatusVar = makeVar<FormsIframeSavingStatus>(FormsIframeSavingStatus.Saved);

export const editingFormFocusCountVar = makeVar<number>(0);

export type CancellationDialogData = {
    isOpen: boolean;
    discardCallback?: () => void;
};

export const cancellationDialogDataVar = makeVar<CancellationDialogData>({ isOpen: false });

export type ChangeStatusDialogData = {
    isOpen: boolean;
    targetStatus?: boolean;
    templateInstanceId?: string;
    templateInstanceTitle?: string;
};

export const changeStatusDialogDataVar = makeVar<ChangeStatusDialogData>({ isOpen: false });

export type StatusChanged = {
    status: boolean;
    templateInstanceId: string;
};

export const statusChangedVar = makeVar<StatusChanged>(null);

export enum Mode {
    None,
    Create,
    Edit,
}

export const currentModeVar = makeVar<Mode>(Mode.None);

export const setEditMode = (templateInstanceId: string, scope: TemplateScope, team?: TeamBasicInfo): void => {
    currentModeVar(Mode.Edit);
    templateInstanceIdVar(templateInstanceId);
    currentScopeInCreationVar(scope);
    currentTeamInCreationVar(team && { ...team });
};

export const setTemplateInstance = (
    instance: ApprovalTemplateInstance | SelfOwnedApprovalTemplateInstance,
    mode: Mode
): void => {
    resetCreateTemplateInstance();

    currentModeVar(mode);
    if (mode === Mode.Edit) {
        currentMaxStepVar(CreateTemplateInstanceWizardSteps.WorkflowSettings);
        // edit valid instance
        updateTemplateInstanceInputCopyVar({
            Category: instance.Category,
            Description: instance.Description,
            AccentColor: instance.AccentColor,
            Id: instance.Id,
            IconName: instance.IconName,
            Title: instance.Title,
            WorkflowSchema: WorkflowSchema2WorkflowSchemaInput(getValidWorkflowSchema(instance.WorkflowSchema)),
            IsVisibleToAll: instance.IsVisibleToAll,
            IsEnabled: instance.IsEnabled,
            VisibleTo: instance.VisibleTo?.map((value: VisibilityPrincipal): VisibilityPrincipalInput => {
                return { PrincipalId: value.PrincipalId, PrincipalType: value.PrincipalType };
            }),
            IsFileAttachmentRequired: instance.IsFileAttachmentRequired,
            IsApproverCommentsRequired: instance.IsApproverCommentsRequired,
            ...('Questions' in instance && { Questions: instance.Questions }),
        });
    }

    if (!instance) {
        return;
    }

    const onlineWorkflowSchemaVersion = getAppHost().getEcsConfig().MicrosoftTeamApprovals.OnlineWorkflowSchemaVersion;
    // If workflow schema enabled and receive v1.0 workflow schema, convert it to v2.0.
    if (
        onlineWorkflowSchemaVersion === 'v2.0' &&
        (!instance.WorkflowSchema.Version || instance.WorkflowSchema.Version === 'v1.0')
    ) {
        instance.WorkflowSchema = WorkflowSchemaConvertor.V1ToV2(instance.WorkflowSchema);
    }
    // If workflow schema enabled and receive v2.0 workflow schema, convert it to v1.0.
    else if (onlineWorkflowSchemaVersion === 'v1.0' && instance.WorkflowSchema.Version === 'v2.0') {
        instance.WorkflowSchema = WorkflowSchemaConvertor.V2ToV1(instance.WorkflowSchema);
    }

    const teamTagEnabled = getAppHost().getEcsConfig().MicrosoftTeamApprovals.TeamTagEnabled;
    // if teamTagenabled, convert static routeType to mixed routetype
    if (teamTagEnabled && instance.WorkflowSchema.RouteTypes === RouteTypes.StaticRoute) {
        instance.WorkflowSchema.RouteTypes = MixedRouteType;
    }
    // handle the situation that disabled the teamtag after template instance with tag had beed created;
    if (!teamTagEnabled && instance.WorkflowSchema.RouteTypes === MixedRouteType) {
        instance.WorkflowSchema.RouteTypes = RouteTypes.StaticRoute;
        instance.WorkflowSchema.DynamicRoute = null;
    }

    templateInstanceTitleVar(instance.Title);
    templateInstanceFormIdVar('FormId' in instance ? instance.FormId : '');
    templateInstanceFormEntriesVar(
        'Questions' in instance && instance.Questions ? instance.Questions.map(mapQuestionToFormEntry) : []
    );
    templateInstanceIconNameVar(instance.IconName);
    templateInstanceIconBackgroundColorVar(instance.AccentColor);
    templateInstanceDescriptionVar(instance.Description);
    templateInstanceCategoryVar(instance.Category);
    if (instance.VisibleTo?.length > 0) {
        currentScopeInCreationVar(TemplateScope.TenantSpecificPeople);
    }
    templateInstanceSpecificPeopleVar(instance.VisibleTo);
    templateInstanceApproverScopeVar(
        instance.WorkflowSchema.Approvers?.length > 0 ? ApproverScope.SpecifyApprovers : ApproverScope.LetSubmitterEnter
    );
    templateInstanceApproversVar(instance.WorkflowSchema.Approvers);
    templateInstanceAllMustRespondVar(instance.WorkflowSchema.AllMustRespond);
    templateInstanceStatusVar(instance.IsEnabled);
    templateInstanceIsCustomResponseEnabledVar(instance.WorkflowSchema.IsCustomResponseEnabled);
    templateInstanceCustomResponseOptionsVar(instance.WorkflowSchema.CustomResponseOptions);
    templateInstanceIsFileAttachmentRequiredVar(instance.IsFileAttachmentRequired);
    templateInstanceWorkflowSchemaVersionVar(instance.WorkflowSchema.Version ?? 'v1.0');
    templateInstanceApproverRouteTypeVar(instance.WorkflowSchema.RouteTypes);
    templateInstanceStaticRouteVar(StaticRoute2UI_StaticRouteInput(instance.WorkflowSchema.StaticRoute));
    templateInstanceDynamicRouteVar(DynamicRoute2UI_DynamicRouteInput(instance.WorkflowSchema.DynamicRoute));
    templateInstanceFallbackPolicyVar(FallbackPolicy2UI_FallbackPolicyInput(instance.WorkflowSchema.FallbackPolicy));
    templateInstanceIsApproverCommentsRequiredVar(instance.IsApproverCommentsRequired);
    templateInstanceStepRoutesVar(StepRouteSetting2UI_StepRouteSetting(instance.WorkflowSchema?.StepRoutes));
};

export const resetCreateTemplateInstance = (): void => {
    currentStepVar(CreateTemplateInstanceWizardSteps.BasicSettings);
    currentMaxStepVar(CreateTemplateInstanceWizardSteps.BasicSettings);

    const onlineWorkflowSchemaVersion = getAppHost().getEcsConfig().MicrosoftTeamApprovals.OnlineWorkflowSchemaVersion;

    templateInstanceIdVar('');
    templateInstanceTitleVar('');
    templateInstanceIconNameVar('');
    templateInstanceIconBackgroundColorVar('');
    templateInstanceDescriptionVar('');
    templateInstanceSpecificPeopleVar([]);
    templateInstanceCategoryVar(ApprovalTemplateCategory.NotSpecified);
    templateInstanceApproversVar([]);
    templateInstanceAllMustRespondVar(true);
    templateInstanceStatusVar(true);
    templateInstanceIsCustomResponseEnabledVar(false);
    templateInstanceCustomResponseOptionsVar([]);
    templateInstanceFormIdVar(null);
    templateInstanceFormEntriesVar([]);
    templateInstanceIsFileAttachmentRequiredVar(false);
    formsIframeSavingStatusVar(FormsIframeSavingStatus.Saved);
    templateInstanceWorkflowSchemaVersionVar(onlineWorkflowSchemaVersion);
    templateInstanceApproverRouteTypeVar(RouteTypes.LetUserEnter);
    templateInstanceStaticRouteVar(DefaultStaticRouteInput);
    templateInstanceDynamicRouteVar(DefaultDynamicRouteInput);
    templateInstanceFallbackPolicyVar(DefaultFallbackPolicyInput);
    blockMainActionVar(false);
    templateInstanceIsApproverCommentsRequiredVar(false);
    templateInstanceStepRoutesVar([]);

    currentModeVar(Mode.None);
};
